import type { UIDataTypes, UIMessage } from "ai";
import dayjs from "dayjs";

import { airlinesLabel } from "./airlines";
import type { EstimatorToolInput, EstimatorToolName } from "./chat-tools";
import { CABIN_GROUP_NAMES, MAX_DESTINATIONS } from "./constants";
import type { EventStart, Origin, UsdRange, Workspace, WorkspaceAction } from "./types";
import { cityKey, homeCities, originKey } from "./workspace";

/** What the browser reports back to the model for one tool call. */
export type EstimatorToolOutput =
  | {
      ok: true;
      applied?: string;
      estimate?: string;
      checked?: ReadCheck;
      windows?: PricedWindow[];
      ranked?: RankedCity[];
      /** Dates used to price candidate cities when the planner had none. */
      assumedDates?: { start: string; end: string };
      /** The planner undid this change from the thread; kept so a reopened thread shows it undone. */
      undone?: boolean;
    }
  | { ok: false; error: string };

/** One priced date window, as the chat shows and reports it. Amounts are in the display currency. */
export interface PricedWindow {
  start: string;
  end: string;
  label: string;
  reason: string;
  cheapestCity: string | null;
  /** Whole trip per person (flights, hotel, meeting space, meals) in the cheapest city. */
  perPersonLow: number | null;
  perPersonHigh: number | null;
  /** Cheapest round-trip fare in that city, with its airline. */
  fare: number | null;
  airline: string | null;
  /** Why the window has no fare (e.g. "Fares aren't published this far ahead yet"); null when priced. */
  fareGap: string | null;
}

/** One candidate city after pricing, cheapest first. Amounts are in the display currency. */
export interface RankedCity {
  city: string;
  reason: string;
  perPersonLow: number | null;
  perPersonHigh: number | null;
  flight: number | null;
  added: boolean;
}

/** What a read_estimate call found, so its caption says only what actually ran. */
export interface ReadCheck {
  hasDates: boolean;
  faresPriced: number;
  faresMissing: number;
  hotelCities: number;
}

type EstimatorUITools = {
  [Name in EstimatorToolName]: { input: EstimatorToolInput<Name>; output: EstimatorToolOutput };
};

/** Metadata on a user message: the attendee file it came with, by name and headcount only. */
export interface EstimatorMessageMetadata {
  /** People is unknown for a document, which the assistant reads. */
  attachment?: { fileName: string; people?: number };
  /**
   * The full instruction behind a suggestion the planner tapped. The thread shows the short label
   * the planner chose; the model reads this instead.
   */
  prompt?: string;
}

/** A message in the estimator chat. */
export type EstimatorUIMessage = UIMessage<EstimatorMessageMetadata, UIDataTypes, EstimatorUITools>;

/**
 * The team a group's travelers in another cabin split off into, keeping their own team so two teams'
 * splits stay apart: "First class", or "Engineering · First class".
 * @param origin - The group being split.
 * @param cabin - The cabin they move to.
 * @returns The split-off team name.
 * @throws Never.
 */
const cabinTeam = (origin: Origin, cabin: Workspace["cabin"]): string =>
  origin.group ? `${origin.group} · ${CABIN_GROUP_NAMES[cabin]}` : CABIN_GROUP_NAMES[cabin];

/**
 * Fills in the rest of a city when the assistant names only the travelers in another cabin ("5 of
 * the 50 from San Diego fly first"): the other 45 keep their row and cabin. When the city already
 * has that cabin's group, the named count resizes it and the rest takes the difference, so the
 * city's total stays the same; naming at least the row and that group folds every split back into
 * one row of the named count.
 * @param origins - The groups the assistant named.
 * @param current - The groups in the estimate now.
 * @param tripCabin - The estimate's default cabin.
 * @returns The named groups, each subset preceded by the rest of its city.
 * @throws Never.
 */
const withRest = (origins: Origin[], current: Origin[], tripCabin: Workspace["cabin"]): Origin[] =>
  origins.flatMap((origin) => {
    const key = originKey(origin);
    const existing = current.find((group) => originKey(group) === key);
    if (!existing || !origin.cabin || origin.cabin === (existing.cabin ?? tripCabin)) return [origin];
    if (origins.filter((named) => originKey(named) === key).length > 1) return [origin];
    const cabinGroup = { city: origin.city, group: cabinTeam(origin, origin.cabin) };
    const moved = current.find((group) => originKey(group) === originKey(cabinGroup));
    const rest = existing.count + (moved?.count ?? 0) - origin.count;
    if (rest < 1) {
      // The whole group moves to this cabin: its own row takes everyone and every cabin split of it
      // goes (setOrigins drops a group whose count is 0).
      const splits = (Object.keys(CABIN_GROUP_NAMES) as Workspace["cabin"][])
        .map((cabin) =>
          current.find(
            (group) => originKey(group) === originKey({ city: origin.city, group: cabinTeam(origin, cabin) })
          )
        )
        .filter((group) => group !== undefined);
      return [origin, ...splits.map((split) => ({ ...split, count: 0 }))];
    }
    return [{ ...existing, count: rest }, moved ? { ...origin, group: cabinGroup.group } : origin];
  });

/**
 * Gives a group's travelers in another cabin their own team ("First class"). A group is one city and
 * team, so without this "45 economy and 5 first from San Diego" would merge into 50 in one cabin.
 * The group in the city's current cabin (the trip's for a new city) keeps the city's own row.
 * @param origins - The groups the assistant named.
 * @param current - The groups in the estimate now.
 * @param tripCabin - The estimate's default cabin.
 * @returns The groups, with a team on each one split off by cabin.
 * @throws Never.
 */
const splitCabins = (origins: Origin[], current: Origin[], tripCabin: Workspace["cabin"]): Origin[] => {
  const cabinOf = (origin: Origin) => origin.cabin ?? tripCabin;
  return origins.map((origin) => {
    const key = originKey(origin);
    const sameKey = origins.filter((named) => originKey(named) === key);
    const row = current.find((group) => originKey(group) === key);
    const rowCabin = row ? cabinOf(row) : tripCabin;
    const keeper = sameKey.find((named) => cabinOf(named) === rowCabin) ?? sameKey[0];
    if (origin === keeper || cabinOf(origin) === cabinOf(keeper)) return origin;
    return { ...origin, group: cabinTeam(origin, cabinOf(origin)) };
  });
};

/**
 * Translates an edit tool's input into workspace actions. Amounts the model gives are in
 * the display currency and are converted to USD here.
 * @param name - Tool name.
 * @param input - Parsed tool input.
 * @param toUsd - Display currency to USD.
 * @param hasRate - Whether a currency can be displayed.
 * @param context - The current workspace and the org's events, for `load_event`.
 * @returns The actions, or an error when a conversion is impossible. Empty for non-edit tools.
 * @throws Never.
 */
export const actionsForTool = (
  name: EstimatorToolName,
  input: unknown,
  toUsd: (amount: number) => number | null,
  hasRate: (currency: string) => boolean,
  context: { workspace: Workspace; events: EventStart[] }
): { actions: WorkspaceAction[] } | { error: string } => {
  switch (name) {
    case "set_origins": {
      const { origins: named, replace } = input as EstimatorToolInput<"set_origins">;
      const { cabin } = context.workspace;
      const current = context.workspace.origins;
      if (replace) return { actions: [{ type: "setOrigins", origins: splitCabins(named, current, cabin) }] };
      const origins = splitCabins(withRest(named, current, cabin), current, cabin);
      // Only the named groups change; every other home city stays.
      const byKey = new Map(origins.map((origin) => [originKey(origin), origin]));
      const kept = current.map((origin) => byKey.get(originKey(origin)) ?? origin);
      const added = origins.filter(
        (origin) => !context.workspace.origins.some((current) => originKey(current) === originKey(origin))
      );
      return { actions: [{ type: "setOrigins", origins: [...kept, ...added] }] };
    }
    case "add_destination":
      return { actions: [{ type: "addDestination", city: (input as EstimatorToolInput<"add_destination">).city }] };
    case "remove_destination":
      return {
        actions: [{ type: "removeDestination", city: (input as EstimatorToolInput<"remove_destination">).city }],
      };
    case "set_dates": {
      const { start, end } = input as EstimatorToolInput<"set_dates">;
      return { actions: [{ type: "setDates", dates: { start, end } }] };
    }
    case "set_assumptions": {
      const parsed = input as EstimatorToolInput<"set_assumptions">;
      const assumptions: Partial<Record<keyof typeof parsed, UsdRange>> = {};
      for (const [key, range] of Object.entries(parsed) as Array<[keyof typeof parsed, UsdRange | undefined]>) {
        if (!range) continue;
        const low = toUsd(range.low);
        const high = toUsd(range.high);
        if (low === null || high === null) return { error: "No exchange rate for the display currency" };
        assumptions[key] = { low, high };
      }
      return { actions: [{ type: "setAssumptions", assumptions }] };
    }
    case "set_trip_details": {
      const { title, cabin, headcount, currency } = input as EstimatorToolInput<"set_trip_details">;
      if (currency && !hasRate(currency)) return { error: `No exchange rate for ${currency}` };
      const actions: WorkspaceAction[] = [];
      if (currency) actions.push({ type: "setCurrency", currency });
      if (title) actions.push({ type: "setTitle", title });
      if (cabin) actions.push({ type: "setCabin", cabin });
      if (headcount) actions.push({ type: "setHeadcount", headcount });
      return { actions };
    }
    case "set_airlines": {
      const { mode, airlines } = input as EstimatorToolInput<"set_airlines">;
      if (mode !== "any" && airlines.length === 0) return { error: "Name at least one airline" };
      const picked = mode === "any" ? [] : airlines;
      return {
        actions: [
          {
            type: "setAirlines",
            airlines: {
              mode,
              codes: picked.map((airline) => airline.code),
              names: Object.fromEntries(picked.map((airline) => [airline.code, airline.name])),
            },
          },
        ],
      };
    }
    case "load_event": {
      const { event_id: eventId } = input as EstimatorToolInput<"load_event">;
      const event = context.events.find((candidate) => candidate.eventId === eventId);
      if (!event) return { error: "That event isn't one of the org's active events" };
      const present = new Set(context.workspace.destinations.map(cityKey));
      const actions: WorkspaceAction[] = [{ type: "setTitle", title: event.name }];
      if (event.dates) actions.push({ type: "setDates", dates: event.dates });
      if (context.workspace.origins.length === 0 && event.headcount > 0) {
        actions.push({ type: "setHeadcount", headcount: event.headcount });
      }
      const room = MAX_DESTINATIONS - context.workspace.destinations.length;
      for (const city of event.destinations.filter((city) => !present.has(cityKey(city))).slice(0, room)) {
        actions.push({ type: "addDestination", city });
      }
      return { actions };
    }
    default:
      return { actions: [] };
  }
};

/**
 * One-line description of an applied edit, for the change row and the model.
 * @param action - The edit.
 * @param workspace - The workspace after it, for counts.
 * @returns e.g. "Added Porto".
 * @throws Never.
 */
export const describeAction = (action: WorkspaceAction, workspace: Workspace): string => {
  switch (action.type) {
    case "addDestination":
      return `Added ${action.city}`;
    case "removeDestination":
      return `Removed ${action.city}`;
    case "setOrigins": {
      const people = workspace.origins.reduce((sum, origin) => sum + origin.count, 0);
      const peopleText = `${people.toLocaleString("en-US")} ${people === 1 ? "person" : "people"}`;
      if (workspace.origins.length === 0) return "Cleared home cities";
      const cities = homeCities(workspace.origins);
      if (cities.length === 1) return `Set home city to ${cities[0]}, ${peopleText}`;
      return `Set ${cities.length} home cities, ${peopleText}`;
    }
    case "setDates":
      return action.dates
        ? `Set dates to ${dayjs(action.dates.start).format("MMM D")} – ${dayjs(action.dates.end).format("MMM D, YYYY")}`
        : "Cleared the dates";
    case "setAssumptions":
      return "Updated the cost assumptions";
    case "setCabin":
      return `Switched to ${action.cabin}`;
    case "setAirlines": {
      const label = airlinesLabel(action.airlines);
      return label ? `Airlines: ${label}` : "Back to any airline";
    }
    case "setHeadcount":
      return `Set headcount to ${action.headcount}`;
    case "setTitle":
      return `Renamed to ${action.title}`;
    case "setCurrency":
      return `Switched to ${action.currency}`;
    case "replace":
      return "Replaced the estimate";
    case "setOriginCount":
      return `Changed a group to ${action.count} ${action.count === 1 ? "person" : "people"}`;
    case "setOriginCabin":
      return action.cabin ? `Switched a group to ${action.cabin}` : "Put a group back on the default cabin";
    case "setFareOverride":
      return action.usd === null
        ? `Back to the live fare to ${action.destination}`
        : `Set your own fare to ${action.destination}`;
    case "setAirport":
      return action.code
        ? `${action.city} flies from ${action.code.toUpperCase()}`
        : `${action.city} back to its main airport`;
  }
};

/**
 * The thread with one tool call's output marked undone.
 * @param messages - The thread.
 * @param toolCallId - The undone call.
 * @returns A new thread; unchanged when the call has no successful output.
 * @throws Never.
 */
export const markUndone = (messages: EstimatorUIMessage[], toolCallId: string): EstimatorUIMessage[] =>
  messages.map((message) =>
    message.role !== "assistant"
      ? message
      : {
          ...message,
          parts: message.parts.map((part) => {
            const tool = part as { toolCallId?: string; state?: string; output?: EstimatorToolOutput };
            if (tool.toolCallId !== toolCallId || tool.state !== "output-available" || !tool.output?.ok) return part;
            return { ...part, output: { ...tool.output, undone: true } } as typeof part;
          }),
        }
  );
